import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FlexRow, TargetResource } from '@app/flex-framework/flex-api.model';
import { FlexService } from '@app/flex-framework/services/flex-framework.service';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SortableResult } from '@app/shared/directives/sortable-list.directive';
import { ResourceType } from '@app/shared/models/core-api.model';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import { Resource } from '@app/target/target-api.model';
import { TrackerService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'dgx-flex-grid',
  styleUrls: ['./flex-grid.component.scss'],
  templateUrl: './flex-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexGridComponent implements OnInit {
  public isEditing: boolean = false;

  @Input() public row: FlexRow;
  @Input() public disablePlanImage: boolean = false;
  @Input() public isReordering: boolean = false;

  @Output() public removeSuggestion = new EventEmitter<{
    event: any;
    row: FlexRow;
    item: Resource;
  }>();
  @Output() public addResource = new EventEmitter<null>();
  @Output() public rowReordered = new EventEmitter<FlexRow>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private flexRowService: FlexRowService,
    private flexService: FlexService,
    private sharedTargetService: SharedTargetService,
    private translateService: TranslateService,
    private trackerService: TrackerService
  ) {}

  public get typeForSkeleton(): string {
    return this.row.flexRowType === 'Input' || this.row.flexRowType === 'Any'
      ? 'tile'
      : this.row.flexRowType.toLowerCase();
  }

  get showAddTile(): boolean {
    if (this.row.flexRowType === 'SkillStandardsSegment') {
      return true;
    }
    return this.isEditing && this.row.flexRowType !== 'Opportunity';
  }

  get showStubCards(): boolean {
    return (
      this.isEditing &&
      ((this.row.targetResources?.length < 3 && !this.row.isViewAll) ||
        (this.row.targetResources?.length > 3 && this.row.isViewAll))
    );
  }

  public get rowResources(): any[] {
    // No extra fancy calculations are needed here, this is done and set by the flex-row-service
    return this.row.targetResources.slice(0, this.row.resourceLimit);
  }

  /** Variable for enabling on-page reordering for specific rows */
  public get isReorderingRow(): boolean {
    return this.isEditing && this.isReordering;
  }

  public ngOnInit(): void {
    // Initial load
    this.isEditing =
      this.activatedRoute.snapshot.queryParams.editMode === 'true';
    this.cdr.markForCheck();

    // Subsequent updates to query params
    this.activatedRoute.queryParams?.subscribe((params) => {
      this.isEditing = params.editMode === 'true';
      this.cdr.markForCheck();
    });
  }

  public getTileType(resource: ResourceType) {
    return this.flexService.getResourceTileType(resource);
  }

  public onAddResource() {
    this.addResource.emit();
  }

  public resourceTrackBy(_, item: Resource) {
    return item.referenceId;
  }

  public onRemoveResourceFromRow(row, item): void {
    const id = item.referenceId || item.providerId;
    this.flexRowService.removeResourceFromRow(row, id);
  }

  /*
   * menu config function for the provider bubble
   */
  public providerBubbleMenuConfigFn(row): (provider: any) => MenuViewModel[] {
    return (provider) => [
      {
        title: this.translateService.instant('dgTagRating_RemoveFromPlan'),
        isHidden: () => !this.isEditing,
        defaultAction: () => this.onRemoveResourceFromRow(row, provider),
      },
    ];
  }

  public onSort = (sorted: SortableResult<Resource>) => {
    const { itemIndex, list } = sorted;
    this.row.loading = true;
    const params = {
      targetId: this.row.section.targetId,
      leftNode: list[itemIndex - 1]?.node ?? null,
      node: list[itemIndex].node,
      rightNode: list[itemIndex + 1]?.node ?? null,
    };

    return this.sharedTargetService.reorderTargetResource(params).pipe(
      switchMap((newNode) => {
        const item = this.row.targetResources.find(
          (resource) => resource.referenceId === list[itemIndex].referenceId
        );
        item.node = newNode;
        return this.flexRowService.getRowResources(this.row, true, true);
      }),
      tap((reorderedResources) => {
        this.row = {
          ...this.flexRowService.updateRowLayout(this.row, this.isEditing),
          loading: false,
          targetResources: [...reorderedResources],
        };

        this.rowReordered.emit(this.row);
      })
    );
  };

  public onIntersectionOfResource(
    item: TargetResource,
    itemIndex: number
  ): void {
    this.trackerService.trackEventData({
      action: 'Content Impressed',
      properties: {
        inputType: item.reference.inputType, // Maps to ContentType in Amplitude
        contentId: item.reference.inputId,
        title: item.reference.title,
        providerId: item.reference.providerId,
        providerName: item.reference.providerName,
        externalId: item.reference.externalId,
        position: itemIndex,
        dateCreated: item.reference.dateCreated,
      },
    });
  }
}
